import { useLocale, useTranslation } from '@multaqa/i18n'
import { Input } from '@multaqa/ui/components/ui/input'
import { MultiSelect } from '@multaqa/ui/components/ui/multi-select'
import { useState } from 'react'
import type { Condition, ConditionField } from './condition-filter-url'
import { useConditionRecordOptions } from './use-record-options'

export function ConditionValueInput({
  field,
  condition,
  onChange,
  companyId,
}: {
  field?: ConditionField
  condition: Condition
  onChange: (value: string | string[]) => void
  companyId: string | null
}) {
  const { t } = useTranslation('condition-filter')
  const { locale } = useLocale()
  const [search, setSearch] = useState('')
  const selectedIds = Array.isArray(condition.value) ? condition.value : []
  const recordOptions = useConditionRecordOptions(
    field?.recordSource,
    search,
    selectedIds,
    companyId,
    locale,
  )
  const hidden =
    !condition.op ||
    ['empty', 'not_empty', 'this_month', 'last_month', 'this_quarter', 'this_year'].includes(
      condition.op,
    )
  if (hidden) return <div aria-hidden="true" />

  if (condition.op === 'between') {
    const value = Array.isArray(condition.value) ? condition.value : ['', '']
    return (
      <div className="grid min-w-0 grid-cols-2 gap-2">
        {(['from', 'to'] as const).map((label, index) => (
          <Input
            key={label}
            aria-label={t(label)}
            type={field?.type === 'date' ? 'date' : 'text'}
            inputMode={field?.type === 'money' || field?.type === 'number' ? 'decimal' : undefined}
            value={value[index] ?? ''}
            onChange={(event) => {
              const next: [string, string] = [value[0] ?? '', value[1] ?? '']
              next[index] = event.target.value
              onChange(next)
            }}
            onKeyDown={submitOnEnter}
          />
        ))}
      </div>
    )
  }

  if (field?.type === 'choice' || field?.type === 'record') {
    const options = (field.type === 'record' ? recordOptions.options : (field.choices ?? [])).map(
      (choice) => ({
        value: choice.value,
        label: t(`choices.${field.key}.${choice.value}`, { defaultValue: choice.label }),
      }),
    )
    const values = Array.isArray(condition.value) ? condition.value : []
    return (
      <div className="min-w-0">
        <MultiSelect
          values={values}
          onValuesChange={onChange}
          options={options}
          onSearchChange={field.type === 'record' ? setSearch : undefined}
          placeholder={t('choose')}
          searchPlaceholder={field.type === 'record' ? t('searchRecords') : t('searchChoices')}
          emptyLabel={field.type === 'record' ? t('noRecords') : t('noChoices')}
          clearLabel={t('clearSelection')}
          aria-label={t('value')}
        />
      </div>
    )
  }

  return (
    <Input
      aria-label={t('value')}
      type={condition.op === 'last_n_days' ? 'number' : field?.type === 'date' ? 'date' : 'text'}
      min={condition.op === 'last_n_days' ? 1 : undefined}
      inputMode={
        field?.type === 'money' || field?.type === 'number' || condition.op === 'last_n_days'
          ? 'decimal'
          : undefined
      }
      value={typeof condition.value === 'string' ? condition.value : ''}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={submitOnEnter}
    />
  )
}

function submitOnEnter(event: React.KeyboardEvent<HTMLInputElement>) {
  if (event.key === 'Enter')
    event.currentTarget
      .closest('[data-condition-editor]')
      ?.querySelector<HTMLButtonElement>('[data-condition-apply]')
      ?.click()
}
